import { useStyle } from "./style";
import { useIntl } from "react-intl";
import {
	Button,
	Divider,
	InfoLabel,
	Input,
	Switch,
	SwitchOnChangeData,
	Text,
} from "@fluentui/react-components";
import { Flex, GapSize } from "@/components/atoms/flex";
import { InputMask } from "@react-input/mask";

import {
	SupervisorQueuesActions,
	SupervisorQueuesState,
	useSupervisorQueuesStore,
} from "@/common/stores/nonpersistingStores/supervisor";
import { isValidTimeSpan } from "@/common/helpers/validationHelper";
import { useMutation } from "@tanstack/react-query";
import { QueueType, SupervisedQueue } from "@/repository/models";
import {
	updateEstimatedWaitTime,
	updateOfferCallsToAgentsWithoutClient,
} from "@/repository/queryFns/supervisor";
import { useCallback } from "react";
import { Edit12Regular } from "@fluentui/react-icons/lib/fonts";
import { ArrowUndo16Regular } from "@fluentui/react-icons";
import { captureError } from "@/common/helpers/sentryHelper";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";

export const SupervisorSettings = () => {
	const intl = useIntl();
	const classes = useStyle();

	const queues = useSupervisorQueuesStore(
		(state: SupervisorQueuesState) => state.queues
	);

	const agentQueues = useQueuesStore((state: QueuesState) => state.queues);

	const canBeEdited = (queueId: string) => {
		const queue = agentQueues.find((x) => x.id === queueId);

		if (queue) {
			return queue.isSupervisor && queue.type != QueueType.Messaging;
		} else {
			return false;
		}
	};

	const supervisedQueuesActions = useSupervisorQueuesStore(
		(actions: SupervisorQueuesActions) => actions
	);

	const queueOfferCallsToAgentsWithoutClientMutation = useMutation({
		mutationFn: (data: SupervisedQueue) =>
			updateOfferCallsToAgentsWithoutClient(data),
		onMutate: (queue: SupervisedQueue): SupervisedQueue => {
			supervisedQueuesActions.update(queue);

			return {
				...queue,
				offerCallsToAgentsWithoutClient: !queue.offerCallsToAgentsWithoutClient,
			};
		},
		onError: (err: any, data: SupervisedQueue, context?: SupervisedQueue) => {
			captureError(
				err,
				"Error updating queue with offer calls to agents without client",
				undefined,
				data
			);
			if (!!context) {
				supervisedQueuesActions.update(context);
			}
		},
	});

	const queueEstimatedWaitTimeMutation = useMutation({
		mutationFn: (data: SupervisedQueue) => updateEstimatedWaitTime(data),
		onMutate: (queue: SupervisedQueue): SupervisedQueue => {
			supervisedQueuesActions.update(queue);

			return {
				...queue,
				useDefinedEstimatedWaitTime: !queue.useDefinedEstimatedWaitTime,
			};
		},
		onError: (err: any, data: SupervisedQueue, context?: SupervisedQueue) => {
			captureError(
				err,
				"Error updating queue with estimated wait time",
				undefined,
				data
			);
			if (!!context) {
				supervisedQueuesActions.update(context);
			}
		},
	});

	const toggleOfferCallsToAgentsWithoutClient = useCallback(
		(id: string, value: boolean) => {
			const queue = queues.find(
				(x: SupervisedQueue) => x.id === id
			) as SupervisedQueue;
			queueOfferCallsToAgentsWithoutClientMutation.mutate({
				...queue,
				offerCallsToAgentsWithoutClient: !!value,
			});
		},
		[queues, supervisedQueuesActions]
	);

	const updateUseDefinedEstimatedWaitTime = (
		queue: SupervisedQueue,
		value: boolean
	) => {
		queueEstimatedWaitTimeMutation.mutate({
			...queue,
			useDefinedEstimatedWaitTime: value,
		});
	};

	const updateDefinedEstimatedWaitTime = (
		queue: SupervisedQueue,
		event: any
	) => {
		if (isValidTimeSpan(event.target.value)) {
			queueEstimatedWaitTimeMutation.mutate({
				...queue,
				definedEstimatedWaitTime: event.target.value,
			});
		}
	};

	return (
		<Flex column gap={GapSize.Medium}>
			<Text className={classes.queuesTitle}>
				{intl.formatMessage({ id: "Supervisor.QueueSettings" })}
			</Text>

			<Divider />

			<div className={classes.queuesList}>
				<Flex gap={GapSize.Medium} className={classes.queuesListHeader}>
					<div className={classes.queueNameHeader}>
						{intl.formatMessage({ id: "Supervisor.QueueSettingsHeader" })}
					</div>
					<div className={classes.estimatedTime}>
						{intl.formatMessage({
							id: "Supervisor.QueueSettingsEstimatedWaitTime",
						})}
					</div>
					<div className={classes.offerCalls}>
						{intl.formatMessage({ id: "Supervisor.QueueSettingsOfferCalls" })}
					</div>
				</Flex>
				<div className={classes.queuesListContainer}>
					{queues.map((queue) => (
						<Flex
							key={queue.id}
							hAlign="center"
							vAlign="center"
							gap={GapSize.Medium}
							className={classes.queuesListItem}
						>
							<div className={classes.queueName}>{queue.name}</div>
							<div className={classes.estimatedTime}>
								{!queue.useDefinedEstimatedWaitTime && (
									<Flex className={classes.estimatedTimeContainer}>
										<Input
											disabled
											value={queue.averageEstimatedWaitTime}
											className={classes.estimatedTimeInput}
										/>

										{canBeEdited(queue.id) && (
											<Button
												appearance="transparent"
												icon={<Edit12Regular />}
												style={{ padding: 0 }}
												onClick={() =>
													updateUseDefinedEstimatedWaitTime(queue, true)
												}
											/>
										)}
									</Flex>
								)}
								{queue.useDefinedEstimatedWaitTime && (
									<Flex className={classes.estimatedTimeContainer}>
										<InputMask<typeof Input>
											key={crypto.randomUUID()}
											component={Input}
											mask="ab:cd:ef"
											replacement={{
												a: /[0-2]/,
												b: /[0-9]/,
												c: /[0-5]/,
												d: /[0-9]/,
												e: /[0-5]/,
												f: /[0-9]/,
											}}
											defaultValue={
												queue.useDefinedEstimatedWaitTime
													? queue.definedEstimatedWaitTime
													: queue.averageEstimatedWaitTime
											}
											appearance="filled-darker"
											onBlur={(value: any) =>
												updateDefinedEstimatedWaitTime(queue, value)
											}
											className={classes.estimatedTimeInput}
											disabled={!canBeEdited(queue.id)}
										/>
										{canBeEdited(queue.id) && (
											<Button
												appearance="transparent"
												icon={<ArrowUndo16Regular />}
												style={{ padding: 0 }}
												onClick={() =>
													updateUseDefinedEstimatedWaitTime(queue, false)
												}
											/>
										)}
									</Flex>
								)}
							</div>
							<Flex
								vAlign={"center"}
								hAlign={"flex-start"}
								className={classes.offerCalls}
							>
								<Switch
									key={queue.id}
									className={classes.offerCallsSwitch}
									checked={queue.offerCallsToAgentsWithoutClient}
									disabled={
										queue.isSimultaneousOfferingEnabled ||
										!canBeEdited(queue.id)
									}
									onChange={(_: any, data: SwitchOnChangeData) =>
										toggleOfferCallsToAgentsWithoutClient(
											queue.id,
											data.checked
										)
									}
								/>
								{queue.isSimultaneousOfferingEnabled && (
									<InfoLabel
										info={
											<>
												{intl.formatMessage({
													id: "Supervisor.InfoAboutSimultaneousOffering",
												})}
											</>
										}
										infoButton={{
											popover: {
												inline: false,
											},
										}}
									></InfoLabel>
								)}
								{queue.type == QueueType.Messaging && (
									<InfoLabel
										info={
											<>
												{intl.formatMessage({
													id: "Supervisor.InfoAboutMessagingQueue",
												})}
											</>
										}
										infoButton={{
											popover: {
												inline: false,
											},
										}}
									></InfoLabel>
								)}
							</Flex>
						</Flex>
					))}
				</div>
			</div>
		</Flex>
	);
};
